var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useCallback, useMemo, useRef } from 'react';
import { Element } from 'react-scroll';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { sleep } from '../../../../../helpers/common';
import { propFunc, propShape, propStr, propNumber, propValidate, propChildren, } from '../../../../../helpers/custom-prop-types';
import { useReactSelect } from '../../../../../helpers/use/ui/useReactSelect';
import InputError from '../../InputError';
import Select2MultipleInputOnlyContainer from './Select2MultipleInputOnlyContainer';
import Select2MultipleInputOnlyLabel from './Select2MultipleInputOnlyLabel';
import Select2MultipleInputOnlyOption from './Select2MultipleInputOnlyOption';
import Select2MultipleInputOnlyRemove from './Select2MultipleInputOnlyRemove';
/**
 * 非同期でオートコンプリート検索も行うreact-selectのラッパーコンポーネント
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const Select2AsyncMultipleInputOnly = (props) => {
    const { changeInputValue: changeInputValueCallback, className, fields, formName, inputName, inputValidate, loadOptions, noOptionsMessage, optionComponent, placeholder, render, tooltipZIndex, } = props;
    const selectRef = useRef();
    const { changeInputValue, errorMessage, fieldPush, handleBlur, handleChange, inputValue, viewValues, } = useReactSelect(changeInputValueCallback, formName, fields, inputName);
    /**
     * キーボード打鍵時のイベント
     * @type {(function(*): void)|*}
     */
    const handleKeyDown = useCallback((e) => __awaiter(void 0, void 0, void 0, function* () {
        // キーコードが229の場合はIME入力中とみなす（Chrome・IE用）
        if (e.keyCode === 229) {
            return;
        }
        // タブ|カンマ|スペースの場合、入力中の値で確定させる
        const preventInputKeys = ['Tab', ',', ' ', '　'];
        if (preventInputKeys.includes(e.key)) {
            e.preventDefault();
            if (inputValue) {
                fieldPush(inputValue);
            }
            return;
        }
        // エンターの場合は選択中の値で入力を確定させる
        if (e.key === 'Enter') {
            e.preventDefault();
            // focusされているoptionのrefを取得
            const { focusedOptionRef } = selectRef.current.select.select.select;
            if (focusedOptionRef) {
                // refからdata-attribute経由で値を取得
                const focusedOptionValue = focusedOptionRef.getAttribute('data-react-select-value');
                if (focusedOptionValue) {
                    fieldPush(focusedOptionValue);
                }
                yield sleep(0);
                selectRef.current.focus();
            }
        }
    }), [fieldPush, inputValue, selectRef]);
    /**
     * カスタムコンポーネント
     * @type {*}
     */
    const components = useMemo(() => ({
        ClearIndicator: null,
        DropdownIndicator: null,
        MultiValueContainer: Select2MultipleInputOnlyContainer,
        MultiValueLabel: Select2MultipleInputOnlyLabel,
        MultiValueRemove: Select2MultipleInputOnlyRemove,
        Option: optionComponent || Select2MultipleInputOnlyOption,
    }), [optionComponent]);
    return (_jsxs(_Fragment, { children: [_jsx(Element, { name: `${fields.name}_view` }), _jsx(AsyncCreatableSelect, { backspaceRemovesValue: true, cacheOptions: true, className: className, classNamePrefix: className, components: components, fields: fields, inputName: inputName, inputValidate: inputValidate, inputValue: inputValue, isClearable: true, isMulti: true, loadOptions: loadOptions, noOptionsMessage: noOptionsMessage, onBlur: handleBlur, onChange: handleChange, onInputChange: changeInputValue, onKeyDown: handleKeyDown, placeholder: placeholder, ref: selectRef, render: render, tooltipZIndex: tooltipZIndex, value: viewValues }), _jsx(InputError, { className: 'field proffit_select_multiple_input_error align-left', error: errorMessage })] }));
};
Select2AsyncMultipleInputOnly.propTypes = {
    changeInputValue: propFunc(),
    className: propStr().isRequired,
    fields: propShape().isRequired,
    formName: propStr().isRequired,
    inputName: propStr().isRequired,
    inputValidate: propValidate().isRequired,
    loadOptions: propFunc().isRequired,
    noOptionsMessage: propFunc(),
    optionComponent: propChildren(),
    placeholder: propStr().isRequired,
    render: propFunc().isRequired,
    tooltipZIndex: propNumber(),
};
Select2AsyncMultipleInputOnly.defaultProps = {
    changeInputValue: undefined,
    noOptionsMessage: undefined,
    optionComponent: undefined,
    tooltipZIndex: undefined,
};
export default Select2AsyncMultipleInputOnly;
